import { ref, unref, computed } from 'vue';
import { invalidateOrgReferenceOptions } from '@/api/orgReferenceApi';
import { getQuickAddRegistryEntry } from '../config/quickAddReferenceRegistry';
import {
  findOptionByNormalizedName,
  upsertOption,
  normalizeReferenceId,
} from '../utils/referenceOptionUtils';

/**
 * Executor for registry-driven quick-add.
 *
 * @param {object} opts
 * @param {string} opts.entity - registry key (department, position, …)
 * @param {import('vue').Ref<object>|object|(() => object)} [opts.context]
 * @param {import('vue').Ref<Array>|Array} [opts.options] - mutable options list to upsert into
 * @param {(option: object) => object} [opts.mapCreatedOption] - reshape before upsert (e.g. label/value)
 * @param {(option: object) => void} [opts.onCreated]
 * @param {(name: string) => void} [opts.onDuplicateSelected]
 */
export function useQuickAddReference({
  entity,
  context = {},
  options = null,
  mapCreatedOption = null,
  onCreated = null,
  onDuplicateSelected = null,
} = {}) {
  const isCreating = ref(false);
  const error = ref('');
  const lastMessage = ref('');

  const registry = getQuickAddRegistryEntry(entity);

  const resolveContext = () => {
    const raw = typeof context === 'function' ? context() : unref(context);
    return raw && typeof raw === 'object' ? raw : {};
  };

  const resolveOptions = () => {
    const list = unref(options);
    return Array.isArray(list) ? list : null;
  };

  function shapeOption(option) {
    if (typeof mapCreatedOption === 'function') {
      return mapCreatedOption(option);
    }
    return option;
  }

  /**
   * Merge a saved catalog row into the bound options list (create or edit).
   * Keeps extra fields from `option` even when mapCreatedOption is a slim mapper.
   * @param {object} option
   * @returns {object}
   */
  function upsertSaved(option) {
    const normalized = {
      ...option,
      id: normalizeReferenceId(option?.id ?? option?.value) ?? option?.id,
      name: option?.name || option?.label || '',
    };
    const mapped = shapeOption(normalized);
    const shaped = {
      ...normalized,
      ...(mapped && typeof mapped === 'object' ? mapped : {}),
      id: mapped?.id ?? mapped?.value ?? normalized.id,
      name: mapped?.name ?? mapped?.label ?? normalized.name,
    };
    const opts = resolveOptions();
    if (opts) {
      upsertOption(opts, {
        ...shaped,
        id: shaped.id ?? shaped.value,
        name: shaped.name ?? shaped.label,
      });
    }
    invalidateOrgReferenceOptions();
    return shaped;
  }

  function assertRequires(ctx) {
    const requires = registry.requires || [];
    for (const key of requires) {
      const value = ctx[key];
      if (value === null || value === undefined || value === '') {
        throw new Error(`Missing required context: ${key}`);
      }
    }
  }

  /**
   * @param {string} rawName
   * @returns {Promise<object>}
   */
  async function create(rawName) {
    if (isCreating.value) {
      throw new Error('Create already in progress');
    }

    error.value = '';
    lastMessage.value = '';

    const name = String(rawName ?? '').trim();
    if (!name) {
      const msg = 'Name is required';
      error.value = msg;
      throw new Error(msg);
    }

    const maxLen = registry.maxLength || 160;
    if (name.length > maxLen) {
      const msg = `Name must be at most ${maxLen} characters`;
      error.value = msg;
      throw new Error(msg);
    }

    const opts = resolveOptions();
    if (opts) {
      const duplicate = findOptionByNormalizedName(opts, name, 'name')
        || findOptionByNormalizedName(opts, name, 'label');
      if (duplicate) {
        lastMessage.value = `"${duplicate.name || duplicate.label}" already exists. Selected instead.`;
        const shaped = shapeOption({
          ...duplicate,
          id: normalizeReferenceId(duplicate.id ?? duplicate.value),
          name: duplicate.name || duplicate.label,
        });
        if (typeof onDuplicateSelected === 'function') {
          onDuplicateSelected(shaped);
        }
        if (typeof onCreated === 'function') {
          onCreated(shaped);
        }
        return shaped;
      }
    }

    const ctx = resolveContext();
    try {
      assertRequires(ctx);
    } catch (err) {
      error.value = err.message || 'Missing required context';
      throw err;
    }

    isCreating.value = true;
    try {
      const payload = registry.buildPayload({ name, context: ctx });
      const response = await registry.create(payload);
      const option = registry.normalize(response);
      option.id = normalizeReferenceId(option.id) ?? option.id;

      const shaped = upsertSaved(option);

      lastMessage.value = `"${option.name}" created and selected.`;
      if (typeof onCreated === 'function') {
        onCreated(shaped);
      }
      return shaped;
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        err?.error?.message ||
        'Failed to create';
      error.value = msg;
      // Always throw Error with the extracted API message (axios interceptor
      // rejects plain objects; AxiosError.message is often a generic status text).
      const wrapped = new Error(msg);
      wrapped.code = err?.response?.data?.error?.code || err?.code || null;
      wrapped.cause = err;
      throw wrapped;
    } finally {
      isCreating.value = false;
    }
  }

  return {
    entity,
    isCreating: computed(() => isCreating.value),
    error: computed(() => error.value),
    lastMessage: computed(() => lastMessage.value),
    create,
    upsertSaved,
    clearError() {
      error.value = '';
    },
  };
}
